import { useParams, Link, Navigate } from "react-router-dom";
import { Clock, ArrowLeft, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { BLOGS } from "@/data/content";

export default function BlogPost() {
  const { slug } = useParams();
  const post = BLOGS.find((b) => b.slug === slug);
  if (!post) return <Navigate to="/blogs" replace />;

  const others = BLOGS.filter((b) => b.slug !== slug).slice(0, 2);

  return (
    <main data-testid="blog-post-page">
      <section className="pt-36 pb-10 max-w-[900px] mx-auto px-6 md:px-10">
        <Reveal>
          <Link to="/blogs" data-testid="back-to-blogs-link" className="inline-flex items-center gap-2 text-sm font-bold text-[#4A5568] hover:text-[#FF5A36] transition-colors duration-300 mb-8">
            <ArrowLeft size={16} /> All stories
          </Link>
          <div className="flex items-center gap-4 mb-6 text-sm text-[#4A5568]">
            <span className="text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-full bg-[#FF5A36]/10 text-[#FF5A36]">
              {post.category}
            </span>
            <span className="flex items-center gap-1"><Clock size={14} /> {post.readTime}</span>
            <span>{post.date}</span>
          </div>
          <h1 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.12] text-[#0B132B]">
            {post.title}
          </h1>
        </Reveal>
      </section>

      <Reveal className="max-w-[1100px] mx-auto px-6 md:px-10">
        <img src={post.img} alt={post.title} className="w-full aspect-[21/10] object-cover rounded-[2rem]" />
      </Reveal>

      <article className="max-w-[760px] mx-auto px-6 md:px-10 py-16 prose-iby" data-testid="blog-post-body">
        {post.body.map((block, i) => {
          if (block.type === "h2") return <h2 key={i}>{block.text}</h2>;
          if (block.type === "quote") return <blockquote key={i}>{block.text}</blockquote>;
          return <p key={i}>{block.text}</p>;
        })}

        <div className="mt-16 bg-white border border-black/5 rounded-[1.75rem] p-8 flex items-center gap-5" data-testid="author-bio-block">
          <img src={post.author.img} alt={post.author.name} className="w-16 h-16 rounded-full object-cover" />
          <div>
            <p className="font-display font-semibold text-lg text-[#0B132B]">{post.author.name}</p>
            <p className="text-sm text-[#4A5568]">{post.author.role} · IBY — Inspired By You</p>
          </div>
        </div>
      </article>

      <section className="max-w-[1100px] mx-auto px-6 md:px-10 pb-24">
        <h2 className="font-display font-semibold text-2xl md:text-3xl tracking-tight text-[#0B132B] mb-10">Keep reading</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {others.map((b) => (
            <Link key={b.slug} to={`/blogs/${b.slug}`} className="group block">
              <div className="overflow-hidden rounded-[1.5rem] mb-5">
                <img src={b.img} alt={b.title} className="w-full aspect-[2/1] object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <h3 className="font-display font-semibold text-lg tracking-tight text-[#0B132B] group-hover:text-[#FF5A36] transition-colors duration-300 flex items-start gap-2">
                {b.title} <ArrowUpRight size={16} className="shrink-0 mt-1" />
              </h3>
            </Link>
          ))}
        </div>
      </section>
    </main>
  );
}
